import Button from 'components/base/Button';
import Section from 'components/base/Section';
import { Col, Form, Row, Spinner } from 'react-bootstrap';
import { useForm, Controller, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import { RootState } from 'redux-state/store';
import { Plus } from 'feather-icons-react';
import { addCouponSchema, AddCouponSchema } from '../model';
import { addCoupon, editCoupon, getCouponById } from 'api/coupon/_requests';
import CouponTargetCard from 'components/cards/CouponTargetCard';

const EditCoupon = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { couponId } = useParams();
  const { user } = useSelector((state: RootState) => state.auth);
  const [isLoading, setIsLoading] = useState(false);
  // const {c}
  const methods = useForm<AddCouponSchema>({
    resolver: zodResolver(addCouponSchema),
    defaultValues: {
      appliesTo: 'all',
      targetIds: [],
      isActive: true
    }
  });

  const toDateInput = (iso?: string | null) =>
    iso ? new Date(iso).toISOString().slice(0, 10) : '';

  useEffect(() => {
    const fetchCoupon = async () => {
      try {
        setIsLoading(true);
        const resp = await getCouponById(couponId as string);
        if (resp) {
          methods.reset({
            code: resp.code,
            discountType: resp.discountType,
            value: resp.value,
            validFrom: toDateInput(resp.validFrom),
            validTo: toDateInput(resp.validTo),
            usageLimit: resp.usageLimit,
            appliesTo: resp.appliesTo as AddCouponSchema['appliesTo'],
            targetIds: resp.targetIds,
            isActive: resp.isActive
          });
        }
      } catch (error) {
        toast.error('Failed to fetch product details');
      } finally {
        setIsLoading(false);
      }
    };

    if (couponId) {
      fetchCoupon();
    }
  }, [couponId, methods]);

  const onSubmit = async (data: AddCouponSchema) => {
    try {
      setIsSubmitting(true);

      const coupon = {
        ...data,
        vendorId: ''
      };
      console.log('coupon', coupon);
      await editCoupon(coupon, couponId as string).then(res => {
        toast.success('coupon Edit successfully');
        navigate(`/profile`);
      });
    } catch (error) {
      toast.error('Failed to edit edit');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDiscard = () => {
    methods.reset();
  };

  return (
    <Section>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)} className="mb-9">
          <div className="d-flex flex-wrap gap-3 flex-between-end mb-5">
            <div>
              <h2 className="mb-2">Add a Coupon</h2>
              <h5 className="text-body-tertiary fw-semibold">
                Add a new Coupon to your store
              </h5>
            </div>
            <div className="d-flex flex-wrap gap-2">
              <Button
                variant="phoenix-secondary"
                type="button"
                onClick={handleDiscard}
              >
                Discard
              </Button>
              <Button variant="primary" type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Publishing...' : 'Publish Coupon'}
              </Button>
            </div>
          </div>
          <Row className="g-5">
            <Col xs={12} xl={8}>
              <div
                className="px-3 py-2 rounded"
                style={{
                  border: '1px solid rgba(0, 0, 0, 0.50)'
                }}
              >
                <div className="mb-4">
                  <h4 className="mb-3">Coupon Code</h4>
                  <Form.Control
                    {...methods.register('code')}
                    placeholder="Write Code here..."
                    className={
                      methods.formState.errors.code ? 'is-invalid' : ''
                    }
                  />
                  {methods.formState.errors.code && (
                    <Form.Control.Feedback type="invalid">
                      {methods.formState.errors.code.message}
                    </Form.Control.Feedback>
                  )}
                </div>
                <Row className="mb-4">
                  <Col xs={12} xl={6}>
                    <div>
                      <h4 className="mb-3">Discount Type</h4>
                      <Form.Select
                        {...methods.register('discountType')}
                        placeholder="select you discount type"
                        className={
                          methods.formState.errors.discountType
                            ? 'is-invalid'
                            : ''
                        }
                      >
                        <option value="">Select your discount type</option>
                        <option value="percentage">Percentage (%)</option>
                        <option value="fixed">Fixed Amount</option>
                      </Form.Select>
                      {methods.formState.errors.discountType && (
                        <Form.Control.Feedback type="invalid">
                          {methods.formState.errors.discountType.message}
                        </Form.Control.Feedback>
                      )}
                    </div>
                  </Col>
                  <Col xl={6} xs={12}>
                    <div className="mb-4">
                      <h4 className="mb-3">Amount</h4>
                      <Form.Control
                        type="number"
                        {...methods.register('value')}
                        placeholder="Write your discount amount..."
                        className={
                          methods.formState.errors.value ? 'is-invalid' : ''
                        }
                      />
                      {methods.formState.errors.value && (
                        <Form.Control.Feedback type="invalid">
                          {methods.formState.errors.value.message}
                        </Form.Control.Feedback>
                      )}
                    </div>
                  </Col>
                </Row>
                <Row className="mb-4">
                  <Col xs={12} xl={6}>
                    <div>
                      <h4 className="mb-3">Valid from</h4>
                      <Form.Control
                        type="date"
                        {...methods.register('validFrom')}
                        placeholder="select date"
                        className={
                          methods.formState.errors.validFrom ? 'is-invalid' : ''
                        }
                      />

                      {methods.formState.errors.validFrom && (
                        <Form.Control.Feedback type="invalid">
                          {methods.formState.errors.validFrom.message}
                        </Form.Control.Feedback>
                      )}
                    </div>
                  </Col>
                  <Col xl={6} xs={12}>
                    <div className="mb-4">
                      <h4 className="mb-3">Valid to</h4>
                      <Form.Control
                        type="date"
                        {...methods.register('validTo')}
                        placeholder="select expiry date..."
                        className={
                          methods.formState.errors.validTo ? 'is-invalid' : ''
                        }
                      />
                      {methods.formState.errors.validTo && (
                        <Form.Control.Feedback type="invalid">
                          {methods.formState.errors.validTo.message}
                        </Form.Control.Feedback>
                      )}
                    </div>
                  </Col>
                </Row>
                <Row className="mb-4">
                  <Col xs={12} xl={6}>
                    <div>
                      <h4 className="mb-3">Usage Limit</h4>
                      <Form.Control
                        type="number"
                        {...methods.register('usageLimit')}
                        placeholder="write you limit..."
                        className={
                          methods.formState.errors.usageLimit
                            ? 'is-invalid'
                            : ''
                        }
                      />

                      {methods.formState.errors.usageLimit && (
                        <Form.Control.Feedback type="invalid">
                          {methods.formState.errors.usageLimit.message}
                        </Form.Control.Feedback>
                      )}
                    </div>
                  </Col>
                </Row>
              </div>
            </Col>
            <Col xs={12} xl={4}>
              <Row
                className="g-2 rounded py-2 px-3 mt-0"
                style={{
                  border: '1px solid rgba(0, 0, 0, 0.50)'
                }}
              >
                <Col xs={12} xl={12}>
                  <CouponTargetCard className="mb-3" />
                </Col>
              </Row>
            </Col>
          </Row>
        </form>
      </FormProvider>
    </Section>
  );
};

export default EditCoupon;
